import { useState } from "react";
import { Card, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useTitle } from "@/hooks/use-title";
import { useTranslation } from "@/i18n/LanguageContext";
import Header from "@/components/header";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useLocation } from "wouter";
import { Loader2 } from "lucide-react";

// Import types
import { CustomPuzzle, User } from "./types";

// Import components
import {
  SolveDialog,
  AnswerDialog,
  RatingDialog,
  PuzzleTabs
} from "./components";

export default function DesafiosCacataisPage() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [, setLocation] = useLocation();
  useTitle(t("desafios_cacatais.title"));

  // Tab state
  const [activeTab, setActiveTab] = useState<string>("unsolved");
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize] = useState<number>(9); // Fixed at 9 items per page
  

  
  // Handle tab change
  const handleTabChange = (newTab: string) => {
    setActiveTab(newTab);
    setCurrentPage(1); // Reset to page 1 when switching tabs
  }
  
  // Dialog states
  const [solvePuzzleId, setSolvePuzzleId] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [answerPuzzleId, setAnswerPuzzleId] = useState<number | null>(null);
  const [isSubmittingAnswer, setIsSubmittingAnswer] = useState(false);
  const [ratingPuzzleId, setRatingPuzzleId] = useState<number | null>(null);
  const [isSubmittingRating, setIsSubmittingRating] = useState(false);

  // Fetch unsolved puzzles count (minimal data for first page only)
  const { data: unsolvedData } = useQuery<{ data: any[], pagination: { totalItems: number } }>({   
    queryKey: ["/api/puzzles/custom/unsolved"],
    queryFn: async () => {
      const response = await fetch(`/api/puzzles/custom?page=1&pageSize=1&unsolved=true`);
      if (!response.ok) {
        return { data: [], pagination: { totalItems: 0 } };
      }
      return response.json();
    },
  });

  // Fetch solved puzzles count (minimal data for first page only)
  const { data: solvedData } = useQuery<{ data: any[], pagination: { totalItems: number } }>({   
    queryKey: ["/api/puzzles/custom/solved"],
    queryFn: async () => {
      const response = await fetch(`/api/puzzles/custom?page=1&pageSize=1&solved=true`);
      if (!response.ok) {
        return { data: [], pagination: { totalItems: 0 } };
      }
      return response.json();
    },
  });

  // Data fetching with pagination based on active tab
  const { data: puzzlesResponse, isLoading, error } = useQuery<{ 
    data: CustomPuzzle[], 
    pagination: { 
      currentPage: number, 
      pageSize: number, 
      totalItems: number, 
      totalPages: number 
    } 
  }>({
    queryKey: ["/api/puzzles/custom", currentPage, pageSize, activeTab],
    queryFn: async () => {
      // Only fetch the type of puzzles needed for the current tab
      const puzzleType = activeTab === "unsolved" ? "unsolved=true" : "solved=true";
      const response = await fetch(`/api/puzzles/custom?page=${currentPage}&pageSize=${pageSize}&${puzzleType}`);
      if (!response.ok) {
        throw new Error('Failed to fetch puzzles');
      }
      return response.json();
    },
  });

  const { data: user } = useQuery<User>({
    queryKey: ["/api/auth/user"],
  });

  // Solve puzzle mutation
  const solvePuzzleMutation = useMutation({
    mutationFn: async ({ puzzleId, privateKey }: { puzzleId: number; privateKey: string }) => {
      const response = await fetch(`/api/puzzles/custom/${puzzleId}/solve`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ private_key: privateKey }),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to solve puzzle");
      }
      
      return response.json();
    },
    onSuccess: () => {
      toast({ 
        title: t("desafios_cacatais.solve_dialog.success"),
        description: t("desafios_cacatais.solve_dialog.success_description"),
      });
      queryClient.invalidateQueries({ queryKey: ["/api/puzzles/custom"] });
      queryClient.invalidateQueries({ queryKey: ["/api/user"] });

      closeDialog();
    },
    onError: (error: Error) => {
      toast({ 
        title: t("desafios_cacatais.solve_dialog.error"),
        description: error.message,
        variant: "destructive",
      });
    },
    onSettled: () => {
      setIsSubmitting(false);
    },
  });

  // Add answer mutation
  const addAnswerMutation = useMutation({
    mutationFn: async ({ puzzleId, content }: { puzzleId: number; content: string }) => {
      const response = await fetch(`/api/puzzles/custom/${puzzleId}/answer`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ content }),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to add answer");
      }
      
      return response.json();
    },
    onSuccess: () => {
      toast({ 
        title: t("desafios_cacatais.answer_dialog.success", { fallback: "Answer added successfully" }),
        description: t("desafios_cacatais.answer_dialog.success_description", { fallback: "Your answer has been added to the puzzle" }),
      });
      queryClient.invalidateQueries({ queryKey: ["/api/puzzles/custom"] });
      closeAnswerDialog();
    },
    onError: (error: Error) => {
      toast({ 
        title: t("desafios_cacatais.answer_dialog.error", { fallback: "Error adding answer" }),
        description: error.message,
        variant: "destructive",
      });
    },
    onSettled: () => {
      setIsSubmittingAnswer(false);
    },
  });

  // Rating mutation
  const ratingMutation = useMutation({
    mutationFn: async ({ puzzleId, rating, comment }: { puzzleId: number; rating: number; comment: string }) => {
      const response = await fetch(`/api/puzzles/custom/${puzzleId}/rate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ 
          rating,
          comment: comment.trim() || null
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to submit rating");
      }
      
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: t("desafios_cacatais.rating_dialog.success", { fallback: "Rating submitted" }),
        description: t("desafios_cacatais.rating_dialog.success_message", { fallback: "Your rating has been submitted successfully" })
      });
      
      queryClient.invalidateQueries({ queryKey: ["/api/puzzles/custom"] });
      closeRatingDialog();
    },
    onError: (error: Error) => {
      toast({
        title: t("desafios_cacatais.rating_dialog.error", { fallback: "Error" }),
        description: error.message,
        variant: "destructive"
      });
    },
    onSettled: () => {
      setIsSubmittingRating(false);
    }
  });

  // Event handlers
  const handleSolvePuzzle = (privateKey: string) => {
    if (!solvePuzzleId) return;
    setIsSubmitting(true);
    solvePuzzleMutation.mutate({ puzzleId: solvePuzzleId, privateKey });
  };
  
  const handleAnswerSubmit = (content: string) => {
    if (!answerPuzzleId) return;
    setIsSubmittingAnswer(true);
    addAnswerMutation.mutate({ puzzleId: answerPuzzleId, content });
  };

  const handleRatingSubmit = (rating: number, comment: string) => {
    if (!ratingPuzzleId) return;
    setIsSubmittingRating(true);
    ratingMutation.mutate({ puzzleId: ratingPuzzleId, rating, comment });
  };

  // Dialog open/close handlers
  const openSolveDialog = (puzzleId: number) => {
    if (!user) {
      toast({ 
        title: t("desafios_cacatais.solve_dialog.auth_required"),
        description: t("desafios_cacatais.solve_dialog.auth_message"),
        variant: "destructive"
      });
      setLocation("/auth");
      return;
    }
    
    setSolvePuzzleId(puzzleId);
  };
  
  const openAnswerDialog = (puzzleId: number) => {
    if (!user) {
      toast({ 
        title: t("desafios_cacatais.answer_dialog.auth_required", { fallback: "Authentication required" }),
        description: t("desafios_cacatais.answer_dialog.auth_message", { fallback: "You need to be logged in to add an answer" }),
        variant: "destructive"
      });
      setLocation("/auth");
      return;
    }
    
    setAnswerPuzzleId(puzzleId);
  };

  const openRatingDialog = (puzzleId: number) => {
    if (!user) {
      toast({ 
        title: t("desafios_cacatais.rating_dialog.auth_required", { fallback: "Authentication required" }),
        description: t("desafios_cacatais.rating_dialog.auth_message", { fallback: "You must be logged in to rate puzzles" }),
        variant: "destructive"
      });
      setLocation("/auth");
      return;
    }
    
    setRatingPuzzleId(puzzleId);
  };

  const closeDialog = () => {
    setSolvePuzzleId(null);
  };
  
  const closeAnswerDialog = () => {
    setAnswerPuzzleId(null);
  };
  
  const closeRatingDialog = () => {
    setRatingPuzzleId(null);
  };

  // Helper function to find user rating for current puzzle
  const getUserRatingForCurrentPuzzle = (): number => {
    if (!ratingPuzzleId || !user || !customPuzzles) return 0;
    
    const puzzle = customPuzzles.find(p => p.id === ratingPuzzleId);
    if (!puzzle?.ratings) return 0;
    
    const userRating = puzzle.ratings.find(r => r.userId === user.id);
    return userRating?.rating || 0;
  };

  // Prepare puzzles from paginated response
  const customPuzzles = puzzlesResponse?.data || [];
  const pagination = puzzlesResponse?.pagination || { 
    currentPage: 1, 
    pageSize: 9, 
    totalItems: 0, 
    totalPages: 0 
  };
  

  
  // Since we're already filtering on the server, just sort the puzzles
  const sortedPuzzles = customPuzzles
    ? [...customPuzzles].sort((a, b) => parseFloat(b.prize) - parseFloat(a.prize))
    : [];
    
  // Assign sorted puzzles to the active tab
  const unsolvedPuzzles = activeTab === "unsolved" ? sortedPuzzles : [];
  const solvedPuzzles = activeTab === "solved" ? sortedPuzzles : [];

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-black">
        <Header />
        <div className="container mx-auto pt-24 px-6 text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto" />
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-black">
        <Header />
        <div className="container mx-auto pt-24 px-6">
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle>Error</CardTitle>
              <CardDescription>Failed to load custom puzzles</CardDescription>
            </CardHeader>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      <main>
        <Header />
        <div className="container mx-auto pt-20 sm:pt-24 px-4 sm:px-6 pb-16 sm:pb-24">
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-white mb-1">{t("desafios_cacatais.title")}</h1>
            <p className="text-zinc-400 text-sm md:text-base">{t("desafios_cacatais.subtitle")}</p>
          </div>
          
          <div className="mb-8">
            <Link href="/puzzles/create">
              <Button className="w-full sm:w-auto bg-red-600 hover:bg-red-700 text-white font-medium py-3 rounded-md transition-colors">
                {t("desafios_cacatais.create_puzzle")}
              </Button>
            </Link>
          </div>

          {/* Show empty state only if there are no puzzles in either tab */}
          {((unsolvedData?.pagination?.totalItems || 0) === 0 && (solvedData?.pagination?.totalItems || 0) === 0) ? (
            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader>
                <CardTitle>{t("desafios_cacatais.no_puzzles_yet")}</CardTitle>
                <CardDescription>{t("desafios_cacatais.be_first")}</CardDescription>
              </CardHeader>
            </Card>
          ) : (
            <PuzzleTabs
              activeTab={activeTab}
              setActiveTab={handleTabChange}
              unsolvedPuzzles={unsolvedPuzzles}
              solvedPuzzles={solvedPuzzles}
              pagination={pagination}
              onPageChange={setCurrentPage}
              user={user}
              onNavigate={setLocation}
              onOpenSolveDialog={openSolveDialog}
              onOpenRatingDialog={openRatingDialog}
              onOpenAnswerDialog={openAnswerDialog}
            />
          )}
          
          {/* Dialogs */}
          <SolveDialog
            isOpen={solvePuzzleId !== null}
            onClose={closeDialog}
            onSubmit={handleSolvePuzzle}
            isSubmitting={isSubmitting}
          />
          
          <AnswerDialog
            isOpen={answerPuzzleId !== null}
            onClose={closeAnswerDialog}
            onSubmit={handleAnswerSubmit}
            isSubmitting={isSubmittingAnswer}
          />
          
          <RatingDialog
            isOpen={ratingPuzzleId !== null}
            onClose={closeRatingDialog}
            onSubmit={handleRatingSubmit}
            isSubmitting={isSubmittingRating}
            initialRating={getUserRatingForCurrentPuzzle()}
          />
        </div>
      </main>
    </div>
  );
}
